import Image from "next/image";
import tovtripLogo from "@assets/icons/sidebar/tovtrip-logo.svg";
import DateTimeNow from "@/components/layout/DateTimeNow";
import {
  appStoreLinks,
  footerSections,
  membershipItems,
  partnerItems,
  paymentItems,
  socialItems,
} from "@/components/layout/footer/footerItems";
import { SiteFrame } from "@/components/layout/SiteFrame";
import { FooterGetAppSection } from "./FooterGetAppSection";
import { FooterSection } from "./FooterSection";
import { Link } from "@/i18n/navigation";
import { Typography } from "@/components/Typography";
import { formatToflag } from "@/lib/utils";

const AppFooter = () => {
  return (
    <>
      <footer
        data-app-footer
        className="w-full overflow-hidden pt-5 relative sm:mb-0 mb-0 md:px-4 px-2 z-10 border-t"
      >
        {/* Background: portrait for mobile, landscape for tablet/desktop */}
        <Image
          src={"/footer/footer_portrait.png"}
          alt=""
          aria-hidden
          fill
          sizes="100vw"
          className="-z-10 object-cover object-bottom md:hidden"
        />
        <Image
          src={"/footer/footer_landscape.png"}
          alt=""
          aria-hidden
          fill
          sizes="100vw"
          className="-z-10 hidden object-cover object-bottom md:block"
        />
        <SiteFrame outerClassName="" innerClassName="flex flex-col">
          <article className="flex flex-col gap-2 md:grid md:grid-cols-4 md:gap-10">
            {/* 1. Branding Tovtrip */}
            <section className="flex flex-col items-start gap-4 text-left md:col-start-1 md:col-end-3 md:row-start-1">
              <div className="flex items-center justify-center">
                <Link href="/">
                  <Image
                    src={tovtripLogo}
                    alt="Tovtrip"
                    width={30}
                    height={30}
                    priority
                    className="h-10 w-auto"
                  />
                </Link>
              </div>
              <div>
                <Typography
                  variant="subtitle1"
                  className="md:max-w-[400px] w-full"
                >
                  ឌីជីថលភាវូបនីយកម្ម ដើម្បីលើកស្ទួយវិស័យទេសចរណ៍កម្ពុជា
                </Typography>
                <Typography variant="body2">
                  ផ្សារភ្ជាប់អ្នកដំណើរជាមួយអាជីវកម្មក្នុងស្រុក
                  ដើម្បីជំរុញវិស័យទេសចរណ៍ប្រកបដោយចីរភាពនៅកម្ពុជា
                </Typography>

                <Typography variant="subtitle1" className="mt-2">
                  Digitalizing to Boost Cambodian Tourism
                </Typography>
                <Typography variant="body2">
                  Connecting travelers with local businesses to drive
                  sustainable tourism in Cambodia
                </Typography>
              </div>

              <div className="flex flex-wrap items-center justify-start gap-4">
                {socialItems.map((item) => (
                  <a
                    key={item.alt}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={item.alt}
                    className="block rounded-full transition hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3BDAFF] focus-visible:ring-offset-2"
                  >
                    <Image
                      src={item.src}
                      alt={item.alt}
                      width={36}
                      height={36}
                      className="h-9 w-9"
                    />
                  </a>
                ))}
              </div>
            </section>

            {/* 2. Footer Section */}
            <div className="border-t my-4 md:border-none" />
            <section className="md:col-start-3 md:col-end-5 md:row-start-1">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-12">
                {footerSections.map((section) => (
                  <FooterSection
                    key={section.title}
                    title={section.title}
                    items={section.items}
                  />
                ))}
              </div>
            </section>

            {/* 3. Footer Get App */}
            <section className="md:col-start-1 md:col-end-3 md:row-start-2">
              <FooterGetAppSection items={appStoreLinks} />
            </section>

            {/* 4. Partners & Payment Methods */}
            <div className="border-t my-4 md:border-none" />
            <section className="flex flex-col gap-6 md:col-start-3 md:row-start-2">
              {/* Our Trusted Partners */}
              <div className="flex flex-col gap-4">
                <Typography variant="subtitle2" className="truncate">
                  Our Trusted Partners | ដៃគូរបស់យើង
                </Typography>
                <div className="flex flex-wrap w-full justify-items-center gap-2 md:w-fit md:justify-items-start">
                  {partnerItems.map((item) => (
                    <Image
                      key={item.alt}
                      src={item.src}
                      alt={item.alt}
                      width={54}
                      height={32}
                      className="h-[32px] w-auto rounded-md"
                    />
                  ))}
                </div>
              </div>

              {/* Payment Method */}
              <div className="flex flex-col gap-4">
                <Typography variant="subtitle2" className="truncate">
                  Payment Method | វិធីសាស្ត្រទូទាត់ប្រាក់
                </Typography>
                <div className="flex flex-wrap w-full justify-items-center gap-y-5 lg:w-fit lg:justify-items-start gap-2">
                  {paymentItems.map((item) => (
                    <Image
                      key={item.alt}
                      src={item.src}
                      alt={item.alt}
                      width={54}
                      height={32}
                      className="h-[27px] w-auto"
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* 5. Memberships & Affiliations */}
            <div className="border-t my-4 md:border-none" />
            <section className="flex flex-col gap-4 md:col-start-4 md:row-start-2">
              <Typography variant="subtitle2" className="truncate">
                Memberships & Affiliations
              </Typography>
              <div className="flex flex-wrap w-full items-center gap-3 md:w-fit">
                {membershipItems.map((item) => (
                  <Image
                    key={item.alt}
                    src={item.src}
                    alt={item.alt}
                    width={44}
                    height={44}
                    className="h-10 w-auto object-contain"
                  />
                ))}
              </div>
            </section>
          </article>

          {/* 6. Copyright */}
          <div className="border-t my-4" />
          <section className="pt-4 pb-8">
            <div className="grid gap-3 md:grid-cols-3 md:items-center">
              <Typography variant="body2" className="text-center md:text-left">
                © <DateTimeNow /> 2026 Tovtrip. Your All-in-One Travel Companion
              </Typography>
              <Typography variant="body2" className="text-center">
                Tovtrip ~ Made in cambodia {formatToflag("KH")}
              </Typography>
              <Typography
                variant="body2"
                className="flex items-center justify-center gap-1 md:justify-end md:text-right"
              >
                ធ្វើដំណើរកម្សាន្តក្នុងស្រុក
                រួមចំណែកគាំទ្រសេដ្ឋកិច្ចនិងពន្លឿនឌីជីថលនៅកម្ពុជា
              </Typography>
            </div>
          </section>
        </SiteFrame>
      </footer>
    </>
  );
};

export default AppFooter;
